/**
 * MobileProjectHeader · Wave Onda1 · 2026-05-18
 *
 * Contextual header for /projects/:id/* routes on mobile (<md).
 * Replaces the full TopBar on small viewports so the PM sees:
 *   LEFT : back arrow + module name (e.g. "RDOs", "Compras", "FVS")
 *   RIGHT: 1 primary icon action (create) + avatar mini-button
 *
 * Second row (when in a project): obra name chip — click opens an
 * info drawer (future) or just scrolls back to project home.
 *
 * Height budget: header row 56px + tabs row 40px = 96px total max.
 *
 * This component is rendered by AppShell when:
 *   - viewport < md (768px)
 *   - pathname matches /projects/:id/*
 *
 * Desktop is unaffected — TopBar continues as-is.
 */

import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/store/auth.store';
import { Avatar } from '@/components/ui/Avatar';
import { cn } from '@/lib/cn';
import { useT } from '@/hooks/useT';

// ─── module label resolution ──────────────────────────────────────────

const MODULE_LABELS_PTBR: Record<string, string> = {
  rdos: 'RDOs',
  logs: 'RDOs',
  fotos: 'Fotos',
  photos: 'Fotos',
  compras: 'Compras',
  orcamento: 'Orçamento',
  trena: 'Trena',
  fvs: 'Vistorias',
  pranchas: 'Pranchas',
  rnc: 'Não conformidades',
  cronograma: 'Cronograma',
  medicoes: 'Medições',
  verba: 'Verba',
  estoque: 'Estoque',
  financeiro: 'Financeiro',
  issues: 'Issues',
  'change-orders': 'Change orders',
  compliance: 'Compliance',
  'curva-abc': 'Curva ABC',
  reports: 'Relatórios',
  atividade: 'Atividade',
  timeline: 'Timeline',
  etapas: 'Etapas',
  'obra-hoje': 'Hoje na obra',
  'visao-geral': 'Visão geral',
};

const MODULE_LABELS_EN: Record<string, string> = {
  rdos: 'RDOs',
  logs: 'RDOs',
  fotos: 'Photos',
  photos: 'Photos',
  compras: 'Purchases',
  orcamento: 'Estimate',
  trena: 'Trena',
  fvs: 'Inspections',
  pranchas: 'Drawings',
  rnc: 'Non-conformances',
  cronograma: 'Schedule',
  medicoes: 'Measurements',
  verba: 'Budget',
  estoque: 'Inventory',
  financeiro: 'Finance',
  issues: 'Issues',
  'change-orders': 'Change orders',
  compliance: 'Compliance',
  'curva-abc': 'ABC curve',
  reports: 'Reports',
  atividade: 'Activity',
  timeline: 'Timeline',
  etapas: 'Stages',
  'obra-hoje': 'Site today',
  'visao-geral': 'Overview',
};

/** Primary CTA per module — icon label + target path fragment */
const MODULE_CTA_PTBR: Record<string, { label: string; icon: string }> = {
  rdos: { label: 'Criar RDO', icon: 'plus' },
  logs: { label: 'Criar RDO', icon: 'plus' },
  fvs: { label: 'Criar vistoria', icon: 'plus' },
  pranchas: { label: 'Enviar prancha', icon: 'upload' },
  fotos: { label: 'Enviar fotos', icon: 'camera' },
  photos: { label: 'Enviar fotos', icon: 'camera' },
  compras: { label: 'Nova compra', icon: 'plus' },
  orcamento: { label: 'Novo item', icon: 'plus' },
};

const MODULE_CTA_EN: Record<string, { label: string; icon: string }> = {
  rdos: { label: 'Create RDO', icon: 'plus' },
  logs: { label: 'Create RDO', icon: 'plus' },
  fvs: { label: 'Create inspection', icon: 'plus' },
  pranchas: { label: 'Upload drawing', icon: 'upload' },
  fotos: { label: 'Upload photos', icon: 'camera' },
  photos: { label: 'Upload photos', icon: 'camera' },
  compras: { label: 'New purchase', icon: 'plus' },
  orcamento: { label: 'New item', icon: 'plus' },
};

// ─── SVG icon atoms ───────────────────────────────────────────────────

function ArrowLeftIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M19 12H5M5 12l7-7M5 12l7 7" stroke="currentColor" strokeWidth="1.8"
        strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="1.8"
        strokeLinecap="round" />
    </svg>
  );
}

function CameraIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 0 2-2l1.58-2H15l2 2h4a2 2 0 0 1 2 2z"
        stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="12" cy="13" r="4" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}

function UploadIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12"
        stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function renderCTAIcon(icon: string) {
  if (icon === 'camera') return <CameraIcon />;
  if (icon === 'upload') return <UploadIcon />;
  return <PlusIcon />;
}

// ─── component ────────────────────────────────────────────────────────

interface MobileProjectHeaderProps {
  projectId: string;
  projectName?: string;
  /** Called when the primary action (create) button is pressed.
   *  If not provided, the button is not rendered. */
  onPrimaryAction?: () => void;
  onOpenAccount?: () => void;
}

export function MobileProjectHeader({
  projectId,
  projectName,
  onPrimaryAction,
  onOpenAccount,
}: MobileProjectHeaderProps): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuth((s) => s.user);
  const { locale } = useT();
  const isEn = locale === 'en';

  // Resolve module from path: /projects/:id/<module>[/...]
  const segments = location.pathname.split('/').filter(Boolean);
  // segments[0]=projects, segments[1]=projectId, segments[2]=module
  const moduleSlug = segments[2] ?? '';
  const moduleLabels = isEn ? MODULE_LABELS_EN : MODULE_LABELS_PTBR;
  const moduleCtaMap = isEn ? MODULE_CTA_EN : MODULE_CTA_PTBR;
  const moduleLabel = moduleLabels[moduleSlug] ?? (isEn ? 'Site' : 'Obra');
  const cta = moduleCtaMap[moduleSlug];

  // Navigate back: if we have a sub-path (e.g. detail within module),
  // go up; otherwise go to project home.
  const handleBack = () => {
    const projectBase = `/projects/${projectId}`;
    const isModuleRoot = location.pathname === `${projectBase}/${moduleSlug}` ||
      location.pathname === projectBase;
    if (isModuleRoot || !moduleSlug) {
      navigate(projectBase);
    } else {
      navigate(-1);
    }
  };

  return (
    <header
      data-testid="mobile-project-header"
      className={cn(
        'sticky top-0 z-30 border-b border-line',
        'bg-surface/90 backdrop-blur-md supports-[backdrop-filter]:bg-surface/70',
        'md:hidden', // only on mobile
      )}
    >
      {/* Row 1: back + module label + actions */}
      <div className="flex h-14 items-center justify-between px-2">

        {/* LEFT: back button + module label */}
        <div className="flex items-center gap-1 min-w-0">
          <button
            type="button"
            onClick={handleBack}
            aria-label={isEn ? 'Back' : 'Voltar'}
            className={cn(
              'flex h-11 w-11 shrink-0 items-center justify-center rounded-lg',
              'text-ink-muted hover:bg-surface-2 hover:text-ink',
              'transition-colors duration-150 active:scale-[0.97]',
            )}
          >
            <ArrowLeftIcon />
          </button>

          <div className="min-w-0">
            <h1
              data-testid="mobile-project-header-module"
              className="truncate text-[15px] font-semibold text-ink leading-tight"
            >
              {moduleLabel}
            </h1>
            {projectName && (
              <Link
                to={`/projects/${projectId}`}
                className="block truncate text-[11px] text-ink-muted leading-tight hover:text-brand-500 transition-colors duration-150"
                data-testid="mobile-project-header-obra"
              >
                {projectName}
              </Link>
            )}
          </div>
        </div>

        {/* RIGHT: primary action + avatar */}
        <div className="flex items-center gap-1 shrink-0">
          {cta && onPrimaryAction && (
            <button
              type="button"
              onClick={onPrimaryAction}
              aria-label={cta.label}
              title={cta.label}
              data-testid="mobile-project-header-cta"
              className={cn(
                'flex h-11 w-11 items-center justify-center rounded-lg',
                'bg-brand-500 text-white',
                'hover:bg-brand-600 transition-colors duration-150',
                'active:scale-[0.97]',
              )}
            >
              {renderCTAIcon(cta.icon)}
            </button>
          )}

          {user && (
            <button
              type="button"
              onClick={onOpenAccount}
              aria-label={isEn ? `Account: ${user.fullName}` : `Conta: ${user.fullName}`}
              data-testid="mobile-project-header-avatar"
              className={cn(
                'flex h-11 w-11 items-center justify-center rounded-full',
                'transition-colors duration-150 active:scale-[0.97]',
              )}
            >
              <Avatar name={user.fullName} src={user.avatarUrl} role={user.role} size="sm" />
            </button>
          )}
        </div>
      </div>
    </header>
  );
}

/**
 * Hook to resolve whether current route is a project sub-route.
 * Returns { isProjectRoute, projectId, moduleSlug }.
 */
export function useProjectRoute() {
  const location = useLocation();
  const match = location.pathname.match(/^\/projects\/([^/]+)(\/([^/]+))?/);
  if (!match) return { isProjectRoute: false, projectId: '', moduleSlug: '' };
  return {
    isProjectRoute: true,
    projectId: match[1] ?? '',
    moduleSlug: match[3] ?? '',
  };
}
